import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Message, Loader, Label, Card, Icon } from 'semantic-ui-react';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { H2, PageSection, Subtitle } from 'common/styles/page';
import { useGetBounceRateStatsQuery } from '../../../../store/generated/admin/messaging-monitoring';
import { format } from 'date-fns';

interface BounceRateStats {
  period: {
    days: number;
    start_date: string;
    end_date: string;
  };
  overall_stats: {
    total_emails: number;
    bounced_emails: number;
    complained_emails: number;
    delivered_emails: number;
    sent_emails: number;
    failed_emails: number;
    pending_emails: number;
    bounce_rate: number;
    complaint_rate: number;
    delivery_rate: number;
  };
  template_stats: Array<{
    template: string;
    total: number;
    bounced: number;
    complained: number;
    delivered: number;
    failed: number;
    bounce_rate: number;
    complaint_rate: number;
    delivery_rate: number;
  }>;
  recent_bounces: Array<{
    id: number;
    email: string;
    template: string;
    status: string;
    error_message: string;
    created_at: string;
  }>;
}

interface BounceRateMonitoringProps {
  selectedDays: number;
  setSelectedDays: (days: number) => void;
}


const BounceRateMonitoring: React.FC<BounceRateMonitoringProps> = ({ selectedDays, setSelectedDays }) => {
  const [refreshing, setRefreshing] = useState(false);

  const { data: stats, error, isLoading, refetch } =
    useGetBounceRateStatsQuery({ days: selectedDays });

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const dayOptions = useMemo(
    () => ([
      { key: 1, text: 'Last 24 hours', value: 1 },
      { key: 7, text: 'Last 7 days', value: 7 },
      { key: 30, text: 'Last 30 days', value: 30 },
      { key: 90, text: 'Last 90 days', value: 90 },
      { key: 365, text: 'Last 1 year', value: 365 },
    ]),
    []
  );

  // Note: header controls are now provided by the parent `MessagingMonitoring` component


  
  const getStatusLabelColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'green';
      case 'sent':
        return 'blue';
      case 'bounced':
        return 'yellow';
      case 'complained':
        return 'orange';
      case 'failed':
        return 'red';
      case 'pending':
        return 'grey';
      default:
        return 'grey';
    }
  };

  const formatPercentage = (value: number) => `${value.toFixed(2)}%`;

  if (isLoading) {
    return (
      <PageSection>
        <Loader active size="large" />
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection>
        <Message negative>
          <Message.Header>Error Loading Data</Message.Header>
          <p>Failed to load bounce rate statistics. Please try again.</p>
        </Message>
      </PageSection>
    );
  }

  const bounceRateStats = stats as BounceRateStats;

  return (
    <PageSection>
      {/* Controls */}
        <PageSection padbottom padtop>


      {/* Overall Statistics */}
      <div style={statsGrid}>
        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Total Emails</H2>
            <div style={value('#333')}>
              {bounceRateStats.overall_stats.total_emails.toLocaleString()}
            </div>
            <div style={sub}>Emails Sent</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>In Flight</H2>
            <div style={value('#1976d2')}>
              {bounceRateStats.overall_stats.sent_emails.toLocaleString()}
            </div>
            <div style={sub}>Awaiting Feedback</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Delivered</H2>
            <div style={value('#2e7d32')}>
              {bounceRateStats.overall_stats.delivered_emails.toLocaleString()}
            </div>
            <div style={sub}>
              {formatPercentage(bounceRateStats.overall_stats.delivery_rate)} Delivery Rate
            </div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Bounced</H2>
            <div style={value('#f57c00')}>
              {bounceRateStats.overall_stats.bounced_emails.toLocaleString()}
            </div>
            <div style={sub}>
              {formatPercentage(bounceRateStats.overall_stats.bounce_rate)} Bounce Rate
            </div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Complained</H2>
            <div style={value('#ff9800')}>
              {bounceRateStats.overall_stats.complained_emails.toLocaleString()}
            </div>
            <div style={sub}>
              {formatPercentage(bounceRateStats.overall_stats.complaint_rate)} Complaint Rate
            </div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Failed</H2>
            <div style={value('#d32f2f')}>
              {bounceRateStats.overall_stats.failed_emails.toLocaleString()}
            </div>
            <div style={sub}>Send Errors</div>
          </Card>
        </div>
      </div>

      </PageSection>


      {/* Template Statistics */}
      <PageSection>
        <Subtitle>Bounce Rates by Template</Subtitle>
        <Table>
          <TableHeader>
            <TableRow>
              <HeaderCell>Template</HeaderCell>
              <HeaderCell>Total</HeaderCell>
              <HeaderCell>In Flight</HeaderCell>
              <HeaderCell>Delivered</HeaderCell>
              <HeaderCell>Bounced</HeaderCell>
              <HeaderCell>Delivery Rate</HeaderCell>
              <HeaderCell>Bounce Rate</HeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bounceRateStats.template_stats.map((template, index) => (
              <TableRow key={index}>
                <TableCell $firstcol style={{ fontWeight: '500' }}>{template.template || 'Unknown'}</TableCell>
                <TableCell>{template.total}</TableCell>
                <TableCell>
                  <Label color="blue">
                    {(template as any).sent || 0}
                  </Label>
                </TableCell>
                <TableCell>{template.delivered}</TableCell>
                <TableCell>{template.bounced}</TableCell>
                <TableCell>
                  <Label color={template.delivery_rate > 90 ? 'green' : 'orange'}>
                    {formatPercentage(template.delivery_rate)}
                  </Label>
                </TableCell>
                <TableCell>
                  <Label color={template.bounce_rate > 5 ? 'yellow' : 'green'}>
                    {formatPercentage(template.bounce_rate)}
                  </Label>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PageSection>
      {/* Recent Bounces */}
      <PageSection padtop='30px'>
        <Subtitle>Recent Bounces and Complaints</Subtitle>
        <Table>
          <TableHeader>
            <TableRow>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Template</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Error Message</HeaderCell>
              <HeaderCell>Date</HeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bounceRateStats.recent_bounces.map((bounce) => (
              <TableRow key={bounce.id}>
                <TableCell $firstcol style={{ fontWeight: '500' }}>{bounce.email}</TableCell>
                <TableCell>{bounce.template || 'Unknown'}</TableCell>
                <TableCell>
                  <Label color={getStatusLabelColor(bounce.status)}>
                    {bounce.status}
                  </Label>
                </TableCell>
                <TableCell style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={bounce.error_message}>
                  {bounce.error_message || 'No error message'}
                </TableCell>
                <TableCell>{format(new Date(bounce.created_at), 'MMM dd, yyyy HH:mm')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PageSection>
    </PageSection>
  );
};

export default BounceRateMonitoring;

const statsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', // exactly 3 columns
  gap: '10px',
  alignItems: 'stretch',
  marginBottom: '24px',
};

const gridItem = { minWidth: 0 }; // allow content to shrink in the cell

const cardStyle = {
  margin: 0,          // kill SUI Card default margins
  width: '100%',      // fill the grid cell
  height: '100%',
  border: '1px solid #d4d4d5',
  borderRadius: '6px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  padding: '12px',
};

const label = { margin: '0 0 8px 0', fontSize: '14px', color: '#666' };
const value = (color = '#333') => ({ fontSize: '24px', fontWeight: 'bold', color });
const sub = { fontSize: '12px', color: '#666' };
